// src/redux/markerCheckerSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MC } from '../types/responses/MC';
import { RootState } from '../../../redux/store';
import { EntityAction } from '../Entities';
import { filteredEntityNames } from '../pages/SortedList';

export const ENTITY_ACTIONS: Record<string, EntityAction> = filteredEntityNames;

export const grouping = ['client', 'savingsaccount', 'loan'];

export const allEntityKeys = Object.keys(ENTITY_ACTIONS);

// Grouped EntityActions
export const groupedEntities: Record<string, string[]> = {};

export const remainingEntities: string[] = [];

allEntityKeys.forEach(key => {
	const lowerKey = key.toLowerCase();
	const matchedGroup = grouping.find(group => lowerKey.includes(group));
	if (matchedGroup) {
		if (!groupedEntities[matchedGroup]) {
			groupedEntities[matchedGroup] = [];
		}
		groupedEntities[matchedGroup].push(key);
	} else {
		remainingEntities.push(key);
	}
});

const entityNames = Object.keys(ENTITY_ACTIONS);

const defaultEntity = grouping.includes(entityNames[0].toLowerCase())
	? (groupedEntities[entityNames[0]]?.[0] ?? entityNames[0])
	: entityNames[0];

type PageIndex = number;

export interface CursorPage<T> {
	items: T[];
}

export interface MarkerCheckerFilters {
	fromDate: string;
	toDate: string;
	status: string;
	action: string[];
	entity: string;
	resourceId: string;
}

export interface MarkerCheckerState {
	activeTab: number;
	activeSubChips: Record<string, string>;
	filters: MarkerCheckerFilters;

	page: number;
	rowsPerPage: number;

	cursors: Array<number | null>;
	pageCache: Record<PageIndex, CursorPage<MC>>;
	nextCursorByPage: Record<PageIndex, number | null>;

	virtualCount: number;
	loading: boolean;
	error: string | null;
}

const initialState: MarkerCheckerState = {
	activeTab: 0,
	activeSubChips: {},
	filters: {
		fromDate: '',
		toDate: '',
		status: '2',
		action: [],
		entity: defaultEntity,
		resourceId: '',
	},
	page: 0,
	rowsPerPage: 50,
	cursors: [null],
	pageCache: {},
	nextCursorByPage: {},
	virtualCount: 0,
	loading: false,
	error: null,
};

const markerCheckerSlice = createSlice({
	name: 'markerChecker',
	initialState,
	reducers: {
		resetMarkerCheckerState: () => initialState,

		resetMarkerCheckerCursorPaging(state) {
			state.page = 0;
			state.cursors = [null];
			state.pageCache = {};
			state.nextCursorByPage = {};
			state.virtualCount = 0;
		},

		setMarkerCheckerRowsPerPage(state, action: PayloadAction<number>) {
			state.rowsPerPage = action.payload;
		},

		setMarkerCheckerPage(state, action: PayloadAction<number>) {
			state.page = action.payload;
		},

		setMarkerCheckerFilters(state, action: PayloadAction<Partial<MarkerCheckerFilters>>) {
			state.filters = { ...state.filters, ...action.payload };
			// reset cursor pages when filters change
			state.page = 0;
			state.cursors = [null];
			state.pageCache = {};
			state.nextCursorByPage = {};
			state.virtualCount = 0;
		},

		setMarkerCheckerActiveTab(state, action: PayloadAction<number>) {
			state.activeTab = action.payload;
		},

		setMarkerCheckerActiveSubChip(
			state,
			action: PayloadAction<{ groupName: string; chipKey: string }>,
		) {
			state.activeSubChips[action.payload.groupName] = action.payload.chipKey;
		},

		setMarkerCheckerPageData(
			state,
			action: PayloadAction<{ page: number; items: MC[]; nextCursor?: number | null }>,
		) {
			const { page, items, nextCursor } = action.payload;
			state.pageCache[page] = { items };
			state.nextCursorByPage[page] = nextCursor ?? null;
			state.cursors[page + 1] = nextCursor ?? null;

			const maxLoaded = Math.max(0, ...Object.keys(state.pageCache).map(n => +n));
			const hasNext = state.nextCursorByPage[maxLoaded] != null;
			state.virtualCount = (maxLoaded + 1) * state.rowsPerPage + (hasNext ? 1 : 0);
		},

		setMarkerCheckerLoading(state, action: PayloadAction<boolean>) {
			state.loading = action.payload;
		},

		setMarkerCheckerError(state, action: PayloadAction<string | null>) {
			state.error = action.payload;
		},
	},
});

export const {
	resetMarkerCheckerState,
	resetMarkerCheckerCursorPaging,
	setMarkerCheckerRowsPerPage,
	setMarkerCheckerPage,
	setMarkerCheckerPageData,
	setMarkerCheckerFilters,
	setMarkerCheckerActiveTab,
	setMarkerCheckerActiveSubChip,
	setMarkerCheckerLoading,
	setMarkerCheckerError,
} = markerCheckerSlice.actions;

export default markerCheckerSlice.reducer;

// Selectors
// selectors
export const selectMarkerCheckerPage = (s: RootState) => s.markerChecker.page;
export const selectMarkerCheckerRowsPerPage = (s: RootState) => s.markerChecker.rowsPerPage;
export const selectMarkerCheckerFilters = (s: RootState) => s.markerChecker.filters;
export const selectMarkerCheckerActiveTab = (s: RootState) => s.markerChecker.activeTab;
export const selectMarkerCheckerActiveSubChip = (s: RootState) => s.markerChecker.activeSubChips;

export const selectMarkerCheckerItems = (s: RootState) => {
	const p = s.markerChecker.page;
	return s.markerChecker.pageCache[p]?.items ?? [];
};
export const selectMarkerCheckerCount = (s: RootState) => s.markerChecker.virtualCount;

export const selectMarkerCheckerCursorForPage = (p: number) => (s: RootState) =>
	s.markerChecker.cursors[p] ?? null;

export const selectMarkerCheckerHasCachedPage = (p: number) => (s: RootState) =>
	Boolean(s.markerChecker.pageCache[p]);
